import React from 'react'
import PropTypes from 'prop-types'
import { Box, Clickable, Icon } from '@maui/elements'
import { CheckboxGroup } from './CheckboxGroup'

export const NestedCheckbox = ({ parentItem, childItems, onChange, isExpanded, renderItem: propsRenderItem, renderParentItem: propsRenderParentItem, childrenDefaultValue, parentDefaultValue }) => {
  const [expanded, setExpanded] = React.useState(isExpanded)
  const [parentChecked, setParentChecked] = React.useState(parentDefaultValue || [])
  const [childValue, setChildValue] = React.useState(childrenDefaultValue || [])

  const renderParentItem = React.useCallback((props) => {
    if (propsRenderParentItem) {
      return propsRenderParentItem(props)
    }
    const { isIndeterminate: indeterminate, parentChecked, ...rest } = props
    return (
      <CheckboxGroup
        inputProps={{
          mr: 'spaceGapMedium',
          indeterminate,
          checked: !!parentChecked,
        }}
        {...rest}
      />
    )
  }, [propsRenderParentItem])

  const renderItem = React.useCallback((props) => {
    if (propsRenderItem) {
      return propsRenderItem(props)
    }
    return (
      <Box ml="40px">
        <CheckboxGroup
          display="flex"
          flexDirection="column"
          gap="spaceGapSmall"
          inputProps={{
            mr: 'spaceGapMedium',
          }}
          {...props}
         />
      </Box>
    )
  }, [propsRenderItem])

  React.useEffect(() => {
    if (onChange) {
      onChange(childValue)
    }
  }, [childValue, onChange])

  const onChangeParent = React.useCallback(() => {
    setParentChecked([])
    if (!parentChecked) {
      setChildValue(childItems.map(item => item.value))
    } else {
      setChildValue([])
    }
  }, [parentChecked, childItems])

  const onChangeChild = React.useCallback((value) => {
    setChildValue(value)
    if (value.length === 0) {
      setParentChecked([])
    } else if (value.length === childItems.length) {
      setParentChecked([])
    }
  }, [setChildValue, childItems])

  const isIndeterminate = React.useMemo(() => {
    return childValue?.length > 0 && childValue.length < childItems.length
  }, [childValue, childItems])

  return (
    <Box display="flex" flexDirection="column" gap="spaceGapSmall">
      <Box display="flex" alignItems="center" gap="spaceGapTiny">
        <Clickable
          variant="text"
          aria-label="Click to expand the nested checkboxes"
          onClick={() => setExpanded(!expanded)}
          color="colorActivePrimary"
          display="flex"
        >
          <Icon name={expanded ? 'chevronDown' : 'chevronRight'} />
        </Clickable>
        {renderParentItem({ onChange: onChangeParent, items: parentItem, parentChecked, isIndeterminate })}
      </Box>
      {
        expanded &&
        <>
          {renderItem({ onChange: onChangeChild, value: childValue, items: childItems })}
        </>
      }
    </Box>
  )
}

NestedCheckbox.propTypes = {
  onChange: PropTypes.func,
  parentItem: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string,
  }),
  childItems: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string,
  })),
  isExpanded: PropTypes.bool,
  renderItem: PropTypes.func,
  renderParentItem: PropTypes.func,
}

NestedCheckbox.defaultProps = {
  isExpanded: false
}
